import railsPostFetch from '@aytm/lib/common/rails-post-fetch'
import { Storage } from '@aytm/lib/storage'

import {
  isSkipperGuidedDraftReport,
  isSkipperGuidedReport
} from '@aytm/app/stats/page/sidebar/reports/lib/skipperGuidedReport'
import { generationReportId } from '@aytm/app/stats/stores/guidedReportGenerationPoll'

const ERROR = 'we are having a hard time completing your request.'

export const STATIC_REPORTS_IDS = ['skipper', 'concept']
export const VIRTUAL_REPORTS_IDS = ['term-report']

const wrappedPostFetch = (url, data = {}) =>
  Storage.Encode(data)
    .then(_bsfz =>
      railsPostFetch(url, {
        body: JSON.stringify({
          _bsfz
        })
      })
    )
    .then(response => response.json())

const getModelData = model => {
  const params = model.current.params
  const config = model.current.config
  const virtual = model.current.virtual
  const schema = model.current.schema
  const storytellings = model.current.storytellings
  const storytellingNotes = model.current.storytellingNotes
  const logicData = model.current.logicData

  const _virtual = Object.entries(null !== virtual ? virtual : {}).reduce((acc, [key, value]) => {
    delete value.preset
    acc[key] = value
    return acc
  }, {})

  const data = {
    reportId: model.id,
    params: params,
    config: config != undefined && null !== config ? config : null,
    virtual: _virtual != undefined && null !== _virtual ? _virtual : null,
    schema: schema != undefined && null !== schema ? schema : null,
    storytellings: storytellings != undefined && null !== storytellings ? storytellings : {},
    storytellingNotes: storytellingNotes != undefined && null !== storytellingNotes ? storytellingNotes : {},
    logicData: logicData != undefined && null !== logicData ? logicData : null
  }

  const scripts = model.current.scripts
  if (scripts && 0 < Object.keys(scripts).length) data.scripts = scripts

  return data
}

const setModelData = (model, listModel) => {
  const initials = model.initials
  const newListModel = JSON.parse(JSON.stringify(listModel))
  if (initials != undefined) {
    const itemIndex = newListModel.findIndex(item => item.id == model.id)

    newListModel[itemIndex].current.params = initials.params
    newListModel[itemIndex].current.config = initials.config
    newListModel[itemIndex].current.virtual = initials.virtual
    newListModel[itemIndex].current.schema = initials.schema
    newListModel[itemIndex].current.logicData = initials.logicData

    const scripts = initials.scripts
    if (scripts && 0 < Object.keys(scripts).length) newListModel[itemIndex].current.scripts = scripts
  }
  return newListModel
}

const decodeModelData = listModel => {
  const skipperReports = listModel.filter(
    (r, index) => 0 != index && 'skipper' == r.current.type && !isSkipperGuidedReport(r.current)
  )
  const conceptReports = listModel.filter((r, index) => 0 != index && 'concept' == r.current.type)
  // index 0 is the pinned Default slot and is kept by the base filter below -- a guided report
  // promoted into it must not also be re-inserted into the guided group, or it renders twice
  const skipperGuidedReports = listModel.filter((r, index) => 0 != index && isSkipperGuidedReport(r.current))
  const _newListModel = JSON.parse(JSON.stringify(listModel)).filter(
    (r, index) => 0 == index || ![...STATIC_REPORTS_IDS, ...VIRTUAL_REPORTS_IDS].includes(r.current.type)
  )
  for (const [index, staticReport] of skipperReports.entries()) {
    _newListModel.splice(index + 1, 0, staticReport)
  }
  for (const [index, staticReport] of conceptReports.entries()) {
    _newListModel.splice(index + skipperReports.length + 1, 0, staticReport)
  }
  for (const [index, guidedReport] of skipperGuidedReports.entries()) {
    _newListModel.splice(index + skipperReports.length + conceptReports.length + 1, 0, guidedReport)
  }
  return _newListModel
}

const encodeModelData = (listModel, newListModel) => {
  const skipperReports = newListModel.filter(
    (r, index) => 0 != index && 'skipper' == r.current.type && !isSkipperGuidedReport(r.current)
  )
  const conceptReports = newListModel.filter((r, index) => 0 != index && 'concept' == r.current.type)
  // virtual entries are dropped by the decode, so they come from the pre-decode list; guided ones
  // survive it and must be read back in the order it holds, or a reorder among them is un-drawn
  const virtualReports = listModel.filter(r => VIRTUAL_REPORTS_IDS.includes(r.current.type))
  const skipperGuidedReports = newListModel.filter((r, index) => 0 != index && isSkipperGuidedReport(r.current))
  const _newListModel = JSON.parse(JSON.stringify(newListModel)).filter(
    (r, index) => 0 == index || ![...STATIC_REPORTS_IDS, ...VIRTUAL_REPORTS_IDS].includes(r.current.type)
  )
  for (const [index, staticReport] of skipperReports.entries()) {
    _newListModel.splice(index + 1, 0, staticReport)
  }
  for (const [index, staticReport] of conceptReports.entries()) {
    _newListModel.splice(index + skipperReports.length + 1, 0, staticReport)
  }
  for (const [index, virtualReport] of virtualReports.entries()) {
    _newListModel.splice(index + skipperReports.length + conceptReports.length + 1, 0, virtualReport)
  }
  for (const [index, guidedReport] of skipperGuidedReports.entries()) {
    _newListModel.splice(
      index + skipperReports.length + conceptReports.length + virtualReports.length + 1,
      0,
      guidedReport
    )
  }
  return _newListModel
}

// A reorder that fails was never persisted, so the optimistic order on screen no longer matches
// the server's. Put the pre-drop list back rather than leave the two disagreeing until a reload.
const restoreListOrder = (listModel, onReportsChange) => onReportsChange(listModel, { type: 'reorder' })

// Re-draw the list from the ordering the reorder answered with, which is authoritative for every
// report it names. A client-only draft has no report id, so it is in neither the request nor that
// ordering -- hold it aside and put it back after the row it followed, or the ordering's length
// cuts it off the list.
const rebuildListOrder = (listModel, data) => {
  const order = data.order.filter((item, pos, self) => self.indexOf(item) == pos)
  const decodedListModel = decodeModelData(listModel)
  const unsentItems = decodedListModel
    .map((item, index) => ({ item, afterId: 0 < index ? decodedListModel[index - 1].id : null }))
    .filter(({ item }) => !(0 < item.id))

  let newListModel = decodedListModel.filter(item => 0 < item.id)

  order.forEach((id, index) => {
    const itemIndex = newListModel.findIndex(item => item.id == id)
    if (0 > itemIndex) {
      newListModel.splice(index, 0, { id, current: data.reports.find(x => x.id == id) })
    } else if (itemIndex != index) {
      const listItem = newListModel[itemIndex]
      newListModel.splice(itemIndex, 1)
      newListModel.splice(index, 0, listItem)
    }
  })
  newListModel.splice(order.length)

  unsentItems.forEach(({ item, afterId }) => {
    const at = newListModel.findIndex(r => r.id == afterId) + 1
    newListModel.splice(0 < at ? at : newListModel.length, 0, item)
  })

  return encodeModelData(listModel, newListModel)
}

const dropListItem = (url, listModel, newListModel, model, onReportsChange, onReportsError) => {
  onReportsChange(newListModel, { type: 'reorder', ids: [] })

  // Dropped back where it started: the list is redrawn, but there is no new order to persist.
  if (newListModel.findIndex(item => item.id == model.id) == listModel.findIndex(item => item.id == model.id)) return

  // Client-only draft placeholders carry a negative id and were never persisted --
  // exclude them from the reorder payload so only real report ids reach the server.
  const order = decodeModelData(newListModel)
    .map(item => item.id)
    .filter(id => 0 < id)

  wrappedPostFetch(url, {
    do: 'reorder',
    order
  })
    .then(data => {
      if (data.order) {
        onReportsChange(rebuildListOrder(listModel, data), { type: 'reorder' })
      }
      if (!data.status) {
        if (!data.order) restoreListOrder(listModel, onReportsChange)
        onReportsError(data.error || ERROR)
      }
    })
    .catch(() => {
      restoreListOrder(listModel, onReportsChange)
      onReportsError(ERROR)
    })
}

export const dropListItemDefault = (props = {}) => {
  const { url, listModel, model, onReportsChange, onReportsError } = props

  if (null === model) return

  const itemIndex = listModel.findIndex(item => item.id == model.id)
  if (0 == itemIndex) return

  const defaultListItem = listModel[0]

  const newListModel = JSON.parse(JSON.stringify(listModel))

  if (STATIC_REPORTS_IDS.includes(defaultListItem.current.type)) {
    newListModel.splice(itemIndex, 1)
    newListModel.splice(0, 1)
    newListModel.splice(0, 0, model)
    newListModel.splice(1, 0, defaultListItem)
  } else {
    const staticReports = listModel.filter(
      r => r.id != model.id && STATIC_REPORTS_IDS.includes(r.current.type) && !isSkipperGuidedReport(r.current)
    )
    const virtualReports = listModel.filter(r => VIRTUAL_REPORTS_IDS.includes(r.current.type))
    // exclude the promoted report, as staticReports does -- counting it here would push the
    // demoted default one slot too far when the report being promoted is itself guided
    const skipperGuidedReports = listModel.filter(r => r.id != model.id && isSkipperGuidedReport(r.current))

    newListModel.splice(itemIndex, 1)
    newListModel.splice(0, 1)
    newListModel.splice(0, 0, model)
    newListModel.splice(
      staticReports.length + virtualReports.length + skipperGuidedReports.length + 1,
      0,
      defaultListItem
    )
  }

  dropListItem(url, listModel, newListModel, model, onReportsChange, onReportsError)
}

export const dropListItemAutomated = (props = {}) => {
  const { url, listModel, model, onReportsChange, onReportsError } = props

  if (null === model) return

  const itemIndex = listModel.findIndex(item => item.id == model.id)
  if (0 != itemIndex) return

  // the report being moved is still at index 0 while this runs, and it satisfies the same
  // predicate -- without excluding it the search picks the mover as its own replacement
  const defaultListItemIndex = listModel.findIndex(
    r =>
      r.id != model.id && !r.current.hidden && ![...STATIC_REPORTS_IDS, ...VIRTUAL_REPORTS_IDS].includes(r.current.type)
  )
  const defaultListItem = listModel[defaultListItemIndex]

  const skipperReports = listModel.filter(
    (r, index) => 0 != index && 'skipper' == r.current.type && !isSkipperGuidedReport(r.current)
  )
  const conceptReports = listModel.filter((r, index) => 0 != index && 'concept' == r.current.type)

  const newListModel = JSON.parse(JSON.stringify(listModel))
  newListModel.splice(defaultListItemIndex, 1)
  newListModel.splice(0, 1)
  newListModel.splice(0, 0, defaultListItem)
  newListModel.splice(
    'skipper' == model.current.type
      ? 1
      : 'concept' == model.current.type
        ? skipperReports.length + 1
        : skipperReports.length + conceptReports.length + 1,
    0,
    model
  )

  dropListItem(url, listModel, newListModel, model, onReportsChange, onReportsError)
}

export const dropListItemOther = (props = {}) => {
  const { url, listIndex, listModel, model, onReportsChange, onReportsError } = props

  if (null === model) return

  const itemIndex = listModel.findIndex(item => item.id == model.id)
  if (0 == itemIndex && 0 == listIndex) return

  const staticReports = listModel.filter(
    (r, index) => 0 != index && STATIC_REPORTS_IDS.includes(r.current.type) && !isSkipperGuidedReport(r.current)
  )
  const virtualReports = listModel.filter(r => VIRTUAL_REPORTS_IDS.includes(r.current.type))
  const skipperGuidedReports = listModel.filter((r, index) => 0 != index && isSkipperGuidedReport(r.current))

  const newListModel = JSON.parse(JSON.stringify(listModel))

  if (0 == itemIndex) {
    const defaultListItemIndex = listModel.findIndex(
      r =>
        r.id != model.id &&
        !r.current.hidden &&
        ![...STATIC_REPORTS_IDS, ...VIRTUAL_REPORTS_IDS].includes(r.current.type)
    )
    const defaultListItem = listModel[defaultListItemIndex]

    newListModel.splice(defaultListItemIndex, 1)
    newListModel.splice(itemIndex, 1)
    newListModel.splice(0, 0, defaultListItem)
    newListModel.splice(
      listIndex + staticReports.length + virtualReports.length + skipperGuidedReports.length + 1,
      0,
      model
    )
  } else {
    newListModel.splice(itemIndex, 1)
    newListModel.splice(
      listIndex + staticReports.length + virtualReports.length + skipperGuidedReports.length + 1,
      0,
      model
    )
  }

  dropListItem(url, listModel, newListModel, model, onReportsChange, onReportsError)
}

export const dropListItemGuided = (props = {}) => {
  const { url, listIndex, listModel, model, onReportsChange, onReportsError } = props

  if (null === model) return

  const itemIndex = listModel.findIndex(item => item.id == model.id)
  // slot 0 is the pinned Default report, not part of the guided group
  if (0 == itemIndex) return

  // the group starts at the list's first guided row: the page lists the virtual reports after every
  // skipper row, guided ones included, so counting the groups ahead lands a drop one virtual row too
  // far -- back where it started, and nothing is sent
  const groupStart = listModel.findIndex((r, index) => 0 != index && isSkipperGuidedReport(r.current))

  const newListModel = JSON.parse(JSON.stringify(listModel))
  newListModel.splice(itemIndex, 1)
  newListModel.splice(groupStart + listIndex, 0, model)

  dropListItem(url, listModel, newListModel, model, onReportsChange, onReportsError)
}

// Client-only guided placeholder skeleton. In the two-phase guided flow the persisted
// StatReport is minted by the generation pipeline once the guided chat completes -- there is
// no session-authed "create draft" call to make here. Negative id marks it local/unsaved; the
// absent origin leaves it guided (see isSkipperGuidedDraftReport). No fabricated metadata --
// the structural skeleton only, with the given status and guided_report snapshot.
const buildGuidedPlaceholderItem = (status, guidedReport, id = null) => {
  const reportId = id || -(Math.floor(Math.random() * 8888888) + 1111111)

  return {
    id: reportId,
    current: {
      id: reportId,
      title: 'New Guided Report',
      type: 'skipper',
      hidden: false,
      protected: false,
      editable: false,
      original: false,
      params: {
        filter_mode: 'basic',
        chart_mode: 'respondents',
        legend: 'long',
        question_logic: 'show',
        question_aliases: 'show',
        rType: 'cases',
        color_theme: '0',
        cl_export_visibility: 'show',
        c1: true
      },
      virtual: null,
      storytellings: null,
      storytellingNotes: null,
      schema: [],
      scripts: null,
      logicData: [],
      config: null,
      guided_report: guidedReport,
      status
    }
  }
}

const guidedGroupPosition = listModel => {
  const staticReports = listModel.filter(
    (r, index) => 0 != index && STATIC_REPORTS_IDS.includes(r.current.type) && !isSkipperGuidedReport(r.current)
  )
  const virtualReports = listModel.filter(r => VIRTUAL_REPORTS_IDS.includes(r.current.type))

  return staticReports.length + virtualReports.length + 1
}

export const addSkipperGuidedListItem = (props = {}) => {
  const { listModel, onReportsChange } = props

  const newListItem = buildGuidedPlaceholderItem('draft', {})

  const newListModel = JSON.parse(JSON.stringify(listModel))
  newListModel.splice(guidedGroupPosition(listModel), 0, newListItem)

  onReportsChange(newListModel, { type: 'add', id: newListItem.id })
}

// Flip a row to "generating" and stamp the launch handle onto its guided_report snapshot. When the
// handle carries the real report id the row also adopts it, so a client-only draft stops being
// client-only: it addresses the persisted StatReport, can be opened, and the id-based status poller
// can track it ('reid' re-keys the row in the reports list and the panel state).
const startRowGeneration = (listModel, onReportsChange, index, generation) => {
  const newListModel = JSON.parse(JSON.stringify(listModel))
  const row = newListModel[index]
  const prevId = row.id
  const reportId = generationReportId(generation)

  row.id = reportId || prevId
  row.current.id = row.id
  row.current.status = 'generating'
  row.current.guided_report = { ...(row.current.guided_report || {}), generation }

  onReportsChange(
    newListModel,
    reportId && reportId != prevId ? { type: 'reid', id: reportId, prevId } : { type: 'update', id: prevId }
  )
  return true
}

export const markSkipperGuidedDraftGenerating = (props = {}) => {
  const { listModel, onReportsChange, generation } = props

  const index = listModel.findIndex(item => isSkipperGuidedDraftReport(item.current))
  if (0 > index) return false // draft already gone -- reloaded, deleted, or switched away

  return startRowGeneration(listModel, onReportsChange, index, generation)
}

// Ensure a row is tracking a (fresh) launch handle: flip an existing draft to generating, flip the
// report a regeneration targets, or create a new generating placeholder when none exists
// (recovering an in-flight generation on page load).
export const ensureSkipperGuidedGeneratingDraft = (props = {}) => {
  const { generation, listModel, onReportsChange, recovered = false } = props

  // The handle's report is already in the list, so that row owns this generation and no other row
  // may take it. Resolved BEFORE the draft branch below: a draft flipped on this handle would adopt
  // an id another row already holds, putting two rows on the list under one id.
  //
  // Whether that row may be flipped turns on where the handle came from, not on what the row
  // currently says. The backend keeps a handle for an hour past its own generation, so one seen on
  // the poll's first response may describe work that already ended -- it is left alone, which is
  // what stops a finished report going back under a spinner after a reload. A handle arriving on
  // any later response appeared while this poll was running, so it is this session's own
  // regeneration and the row's settled status is the stale half of the pair; flipping it is the
  // whole point. The poll fires once per generation id and only while the handle is fresh, so the
  // flip cannot repeat and a long-dead handle never reaches here.
  const reportId = generationReportId(generation)
  const existing = reportId ? listModel.findIndex(item => reportId == item.id) : -1
  if (0 <= existing) {
    if (recovered) return false
    return startRowGeneration(listModel, onReportsChange, existing, generation)
  }

  // The backend never clears the launch handle, so a handle stays "fresh" for 60 minutes after
  // its report has already landed. Without this guard, reloading after a completed generation
  // would resurrect a phantom generating row next to the finished report -- or flip a waiting
  // draft onto a generation that has already ended somewhere else in the list.
  const generationId = generation?.generation_id
  if (
    generationId &&
    0 <= listModel.findIndex(item => 0 < item.id && generationId == item.current.guided_report?.generation_id)
  ) {
    return
  }

  // A draft is still awaiting its handle -- flip it (reuses the tested path).
  if (0 <= listModel.findIndex(item => isSkipperGuidedDraftReport(item.current))) {
    return markSkipperGuidedDraftGenerating(props)
  }
  // Already tracking a generating placeholder -- nothing to add.
  if (0 <= listModel.findIndex(item => 'skipper' == item.current.type && 'generating' == item.current.status)) return

  const newListItem = buildGuidedPlaceholderItem('generating', { generation }, reportId)

  const newListModel = JSON.parse(JSON.stringify(listModel))
  newListModel.splice(guidedGroupPosition(listModel), 0, newListItem)

  // select: false -- a generation recovered on mount must not relocate the user. The add action
  // selects the row it adds, which is what the Create button depends on.
  onReportsChange(newListModel, {
    type: 'add',
    id: newListItem.id,
    status: 'generating',
    select: false
  })
}

// Swap the placeholder skeleton for the report the generation poll carries: id, title, body,
// share link -- the same object the page bootstrap serves. `status` is stripped because it is the
// client-side lifecycle flag here ('draft' / 'generating' / 'ready'), owned by the caller, while
// the server publishes its own in-flight generation status under the same key. The local
// guided_report keeps its launch handle (the poll key and the reload guard read it) and takes the
// server's snapshot on top.
const mergeGuidedReportStructure = (row, report) => {
  if (!report) return row

  const { status: _status, id: incomingId, ...incoming } = report
  // a report the poll has not persisted yet carries no usable id -- the row keeps the one it has
  const structure = Number.isInteger(incomingId) && 0 < incomingId ? { ...incoming, id: incomingId } : incoming
  const guided = { ...(row.current.guided_report || {}), ...(structure.guided_report || {}) }

  row.current = { ...row.current, ...structure, guided_report: guided }
  if (0 < row.current.id) row.id = row.current.id
  return row
}

// Set the status of the in-session generating guided draft, targeting the row for THIS
// generation's report by id (the row adopts the real report id once the launch handle carries
// it). Falls back to the sole in-flight draft when the id is unknown or still temporary, but
// never guesses among several. Merges the persisted report when one is given. Returns whether a
// row was updated. No-ops when the draft is gone (reloaded, deleted, or switched away).
const setGuidedGeneratingDraftStatus = (listModel, onReportsChange, status, reportId = null, report = null) => {
  const generating = item => 'skipper' == item.current.type && 'generating' == item.current.status
  // Only a guided row may be *guessed* at. The auto-launched report is served 'generating' for its
  // own regeneration, so without provenance the guess can demote a report that is never deletable.
  const guessable = item => generating(item) && isSkipperGuidedReport(item.current)

  let index = null != reportId ? listModel.findIndex(item => generating(item) && reportId == item.id) : -1
  if (0 > index && 1 == listModel.filter(guessable).length) index = listModel.findIndex(guessable)
  if (0 > index) return false

  const newListModel = JSON.parse(JSON.stringify(listModel))
  const row = newListModel[index]
  const prevId = row.id

  mergeGuidedReportStructure(row, report)
  row.current.status = status

  onReportsChange(
    newListModel,
    row.id != prevId ? { type: 'reid', id: row.id, prevId } : { type: 'update', id: row.id }
  )
  return true
}

// Adopt the persisted report while the generation is still running: the stat server saves the row
// before enqueueing the job, so the poll hands it over mid-flight. The row stops being client-only
// (real id, server body) and stays 'generating' until the terminal tick.
export const adoptSkipperGuidedDraftReport = (props = {}) => {
  const { listModel, onReportsChange, reportId = null, report = null } = props
  return setGuidedGeneratingDraftStatus(listModel, onReportsChange, 'generating', reportId, report)
}

// Flip the generating guided draft to a "ready" state once the job poll reports completion,
// merging the finished report the same poll carries.
export const markSkipperGuidedDraftReady = (props = {}) => {
  const { listModel, onReportsChange, reportId = null, report = null } = props
  return setGuidedGeneratingDraftStatus(listModel, onReportsChange, 'ready', reportId, report)
}

export const addListItem = (dispatch, props = {}) => {
  const { url, listModel, model, onReportsChange, onReportsError } = props

  if (Object.hasOwn(model, 'id'))
    dispatch({
      type: 'UPDATE_REPORT_STATE',
      request: {
        id: model.id,
        status: 'add',
        process: 'add'
      }
    })

  const data = getModelData(model)

  wrappedPostFetch(url, {
    do: 'add',
    share: data
  })
    .then(data => {
      if (!Number.isNaN(Number(data.id)) && 0 < Number(data.id)) {
        const staticReports = listModel.filter(
          (r, index) => 0 != index && STATIC_REPORTS_IDS.includes(r.current.type) && !isSkipperGuidedReport(r.current)
        )
        const skipperGuidedReports = listModel.filter((r, index) => 0 != index && isSkipperGuidedReport(r.current))

        const newListItem = { id: data.id, current: data }

        let newListModel = decodeModelData(Object.hasOwn(model, 'id') ? setModelData(model, listModel) : listModel)
        newListModel.splice(staticReports.length + skipperGuidedReports.length + 1, 0, newListItem)
        newListModel = encodeModelData(listModel, newListModel)

        onReportsChange(newListModel, {
          type: 'add',
          id: newListItem.id,
          siblingId: Object.hasOwn(model, 'id') ? model.id : null,
          callback: () => {
            dispatch({
              type: 'UPDATE_REPORT_STATE',
              request: {
                id: model.id,
                status: null,
                process: null
              }
            })
            dispatch({
              type: 'UPDATE_REPORT_STATE',
              request: {
                id: data.id,
                status: 'active',
                process: null
              }
            })
          }
        })
      } else {
        onReportsError(data.error || ERROR)

        dispatch({
          type: 'UPDATE_REPORT_STATE',
          request: {
            id: model.id,
            status: null,
            process: null
          }
        })
      }
    })
    .catch(() => {
      onReportsError(ERROR)

      dispatch({
        type: 'UPDATE_REPORT_STATE',
        request: {
          id: model.id,
          status: null,
          process: null
        }
      })
    })
}

export const updateListItem = (dispatch, props = {}) => {
  const { url, listModel, model, onReportsChange, onReportsError } = props

  dispatch({
    type: 'UPDATE_REPORT_STATE',
    request: {
      id: model.id,
      status: null,
      process: 'save'
    }
  })

  const data = getModelData(model)
  data.edited = model.current['edited'] || model.current['created']

  wrappedPostFetch(url, {
    do: 'update',
    id: model.id,
    share: data
  })
    .then(data => {
      if (!Number.isNaN(Number(data.id)) && 0 < Number(data.id)) {
        const newListModel = JSON.parse(JSON.stringify(listModel))
        const itemIndex = newListModel.findIndex(item => item.id == data.id)
        newListModel[itemIndex].current.edited = data.edited
        newListModel[itemIndex].current.editor = data.editor

        onReportsChange(newListModel, { type: 'update', id: model.id })
      } else {
        onReportsError(data.error || ERROR, {
          report: data.report
        })
      }

      dispatch({
        type: 'UPDATE_REPORT_STATE',
        request: {
          id: model.id,
          process: null
        }
      })
    })
    .catch(() => {
      onReportsError(ERROR)

      dispatch({
        type: 'UPDATE_REPORT_STATE',
        request: {
          id: model.id,
          process: null
        }
      })
    })
}

export const deleteListItem = (dispatch, props = {}) => {
  const { url, listModel, model, onReportsChange, onReportsError } = props

  // A Skipper guided draft is a client-only placeholder (negative id, never persisted) --
  // no backend row to delete, so drop it from the list locally instead of round-tripping.
  if (isSkipperGuidedDraftReport(model.current)) {
    const withoutItem = JSON.parse(JSON.stringify(listModel)).filter(item => item.id != model.id)
    onReportsChange(encodeModelData(withoutItem, decodeModelData(withoutItem)), { type: 'delete', id: model.id })
    return
  }

  dispatch({
    type: 'UPDATE_REPORT_STATE',
    request: {
      id: model.id,
      process: 'delete'
    }
  })

  wrappedPostFetch(url, {
    do: 'delete',
    id: model.id
  })
    .then(data => {
      if (data.status) {
        dispatch({
          type: 'UPDATE_REPORT_STATE',
          request: {
            id: model.id,
            status: 'deleted'
          }
        })

        setTimeout(() => {
          let newListModel = JSON.parse(JSON.stringify(listModel))
          const itemIndex = newListModel.findIndex(item => item.id == model.id)
          if (0 < itemIndex) newListModel.splice(itemIndex, 1)
          else {
            const newItemIndex = newListModel.findIndex(
              (r, index) =>
                0 < index &&
                !r.current.hidden &&
                ![...STATIC_REPORTS_IDS, ...VIRTUAL_REPORTS_IDS].includes(r.current.type)
            )
            newListModel.splice(0, 1, newListModel.splice(newItemIndex, 1)[0])
          }
          newListModel = encodeModelData(listModel, decodeModelData(newListModel))
          onReportsChange(newListModel, { type: 'delete', id: model.id })
        }, 500)
      } else {
        if (data.report) {
          const listItem = { id: data.report.id, current: data.report }

          const newListModel = JSON.parse(JSON.stringify(listModel))
          const itemIndex = newListModel.findIndex(item => item.id == listItem.id)
          if (0 <= itemIndex) newListModel.splice(itemIndex, 1)
          newListModel.splice(itemIndex, 0, listItem)

          onReportsChange(newListModel, { type: 'update', id: listItem.id })
        }

        onReportsError(data.error || ERROR)

        dispatch({
          type: 'UPDATE_REPORT_STATE',
          request: {
            id: model.id,
            process: null
          }
        })
      }
    })
    .catch(() => {
      onReportsError(ERROR)

      dispatch({
        type: 'UPDATE_REPORT_STATE',
        request: {
          id: model.id,
          process: null
        }
      })
    })
}

export const updateItemModel = (dispatch, props = {}) => {
  const { url, data, updates, listModel, model, onReportsChange, onReportsError } = props

  dispatch({
    type: 'UPDATE_REPORT_STATE',
    request: {
      id: model.id,
      process: 'update'
    }
  })

  data.edited = model.current['edited'] || model.current['created']

  wrappedPostFetch(url, data)
    .then(data => {
      if (data.status) {
        const newListModel = JSON.parse(JSON.stringify(listModel))
        const itemIndex = newListModel.findIndex(item => item.id == model.id)
        updates.forEach(x => (newListModel[itemIndex].current[x.path] = x.value))
        newListModel[itemIndex].current.edited = data.edited
        newListModel[itemIndex].current.editor = data.editor

        onReportsChange(newListModel, { type: 'update_props', id: model.id })
      } else {
        onReportsError(data.error || ERROR, {
          report: data.report
        })
      }

      dispatch({
        type: 'UPDATE_REPORT_STATE',
        request: {
          id: model.id,
          process: null
        }
      })
    })
    .catch(() => {
      onReportsError(ERROR)

      dispatch({
        type: 'UPDATE_REPORT_STATE',
        request: {
          id: model.id,
          process: null
        }
      })
    })
}

export const updateItemShareLink = (dispatch, props = {}) => {
  const { url, share, listModel, model, onReportsChange, onReportsError } = props

  const process = share ? 'share' : 'unshare'

  dispatch({
    type: 'UPDATE_REPORT_STATE',
    request: {
      id: model.id,
      process
    }
  })

  wrappedPostFetch(url, { do: process, id: model.id, edited: model.current['edited'] || model.current['created'] })
    .then(data => {
      if (data.status) {
        const newListModel = JSON.parse(JSON.stringify(listModel))
        const itemIndex = newListModel.findIndex(item => item.id == model.id)
        newListModel[itemIndex].current.shareLink = data.shareLink
        newListModel[itemIndex].current.edited = data.edited
        newListModel[itemIndex].current.editor = data.editor
        onReportsChange(newListModel, { type: 'update_props', id: model.id })
      } else {
        onReportsError(data.error || ERROR, {
          report: data.report
        })
      }

      dispatch({
        type: 'UPDATE_REPORT_STATE',
        request: {
          id: model.id,
          process: null
        }
      })
    })
    .catch(() => {
      onReportsError(ERROR)

      dispatch({
        type: 'UPDATE_REPORT_STATE',
        request: {
          id: model.id,
          process: null
        }
      })
    })
}

export const revertListItem = (dispatch, props = {}) => {
  const { listModel, model, onReportsChange } = props

  dispatch({
    type: 'UPDATE_REPORT_STATE',
    request: {
      id: model.id,
      status: null,
      process: 'revert'
    }
  })

  const newListModel = setModelData(model, listModel)
  onReportsChange(newListModel, { type: 'revert', id: model.id })

  dispatch({
    type: 'UPDATE_REPORT_STATE',
    request: {
      id: model.id,
      process: null
    }
  })
}

export const RenameAllCodebooks = (url, codebook, tags, reports, callback) => {
  wrappedPostFetch(url, {
    do: 'rename_codebook',
    from: codebook.from,
    to: codebook.to,
    tags,
    reports
  })
    .then(data => callback(data))
    .catch(error => callback({ status: false, error }))
}

export const RenameAllTags = (url, tag, reports, callback) => {
  wrappedPostFetch(url, {
    do: 'rename_tag',
    from: tag.from,
    to: tag.to,
    reports
  })
    .then(data => callback(data))
    .catch(error => callback({ status: false, error }))
}
